/**
 * 出発地・到着地の入力
 *
 * 名前だけでは最適化に渡せないので、現在地・マイプレイス・Google の検索結果から座標を確定させる。
 * 入力欄にフォーカスすると候補を出し、検索は従量課金なので検索ボタンか Enter で明示的に押したときだけ引く。
 */

import { Crosshair, Home, Loader2, MapPin, Search } from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { getMyPlaces } from '@/api/services/overland'
import { searchRallyPlaces } from '@/api/services/stamp-rally'
import type { MyPlace } from '@/api/types/overland'
import type { PlaceSearchResult } from '@/api/types/stamp-rally'
import { VisitedPlaceBadge } from '@/components/shared/visited-place-badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useCurrentPosition } from '@/hooks/useCurrentPosition'

export type PlanPoint = {
  name: string
  latitude: number | null
  longitude: number | null
}

const OptionRow = ({
  icon,
  label,
  hint,
  badge,
  disabled = false,
  onClick,
}: {
  icon: ReactNode
  label: string
  hint?: string | null
  badge?: ReactNode
  disabled?: boolean
  onClick: () => void
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className="flex w-full items-center gap-2 border-b border-border/50 px-3! py-2! text-left last:border-b-0 disabled:opacity-50"
  >
    <span className="shrink-0 text-muted-foreground">{icon}</span>
    <span className="min-w-0 flex-1">
      <span className="flex items-center gap-1.5">
        <span className="min-w-0 truncate text-sm">{label}</span>
        {badge}
      </span>
      {hint && <span className="block truncate text-xs text-muted-foreground">{hint}</span>}
    </span>
  </button>
)

const SectionLabel = ({ children }: { children: ReactNode }) => (
  <p className="border-b border-border/50 bg-muted/40 px-3 py-1 text-xs text-muted-foreground">{children}</p>
)

const MyPlaceOptions = ({
  places,
  isLoading,
  onPick,
}: {
  places: MyPlace[]
  isLoading: boolean
  onPick: (place: MyPlace) => void
}) => {
  if (isLoading) {
    return (
      <p className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        読み込み中
      </p>
    )
  }
  if (places.length === 0) {
    return <p className="px-3 py-2 text-sm text-muted-foreground">該当するマイプレイスがありません</p>
  }
  return (
    <>
      {places.map((place) => (
        <OptionRow key={place.id} icon={<Home className="size-4" />} label={place.name} onClick={() => onPick(place)} />
      ))}
    </>
  )
}

type Props = {
  id: string
  /** 周りで見出しを出しているときは省略できる */
  label?: string
  placeholder: string
  value: PlanPoint
  onChange: (next: PlanPoint) => void
}

export const PlanPointField = ({ id, label, placeholder, value, onChange }: Props) => {
  const { request, isLoading: isLocating } = useCurrentPosition()
  const rootRef = useRef<HTMLDivElement>(null)
  /** 検索語 → 結果。従量課金なので同じ語は 1 回しか引かない */
  const searchCache = useRef(new Map<string, PlaceSearchResult[]>())
  const [isOpen, setIsOpen] = useState(false)
  const [results, setResults] = useState<PlaceSearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  /** null は未取得。候補を開いたときに 1 回だけ引く */
  const [myPlaces, setMyPlaces] = useState<MyPlace[] | null>(null)
  const [isLoadingMyPlaces, setIsLoadingMyPlaces] = useState(false)

  // 候補内のスクロールを殺したくないので、blur ではなく外側のタップで閉じる
  useEffect(() => {
    if (!isOpen) return
    const handlePointerDown = (e: PointerEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return
      setIsOpen(false)
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [isOpen])

  const query = value.name.trim()
  const filteredPlaces = (myPlaces ?? []).filter((place) => !query || place.name.includes(query))

  const handleFocus = async () => {
    setIsOpen(true)
    if (myPlaces !== null || isLoadingMyPlaces) return
    setIsLoadingMyPlaces(true)
    try {
      setMyPlaces(await getMyPlaces())
    } catch {
      toast.error('マイプレイスの取得に失敗しました')
      setMyPlaces([])
    } finally {
      setIsLoadingMyPlaces(false)
    }
  }

  const commit = (next: PlanPoint) => {
    setResults([])
    setIsOpen(false)
    onChange(next)
  }

  const handleUseCurrent = async () => {
    const coords = await request()
    if (!coords) return
    commit({ name: '現在地', latitude: coords.latitude, longitude: coords.longitude })
  }

  const applyResults = (found: PlaceSearchResult[]) => {
    setResults(found)
    if (found.length === 0) toast.info('見つかりませんでした')
  }

  const handleSearch = async () => {
    if (!query) return
    // 同じ語での連打はそのぶん課金されるだけなので引き直さない
    const cached = searchCache.current.get(query)
    if (cached) {
      applyResults(cached)
      return
    }
    setIsSearching(true)
    try {
      const found = await searchRallyPlaces(query)
      searchCache.current.set(query, found)
      applyResults(found)
    } catch {
      toast.error('場所の検索に失敗しました')
    } finally {
      setIsSearching(false)
    }
  }

  return (
    <div ref={rootRef} className="space-y-1.5">
      {label && <Label htmlFor={id}>{label}</Label>}
      <div className="flex gap-2">
        <Input
          id={id}
          value={value.name}
          placeholder={placeholder}
          onFocus={() => void handleFocus()}
          onChange={(e) => {
            setResults([])
            onChange({ ...value, name: e.target.value })
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            e.preventDefault()
            void handleSearch()
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="shrink-0"
          aria-label="この名前で検索"
          disabled={!query || isSearching}
          onClick={() => void handleSearch()}
        >
          {isSearching ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
        </Button>
      </div>

      {isOpen && (
        <div className="max-h-64 overflow-y-auto rounded-lg border">
          <OptionRow
            icon={isLocating ? <Loader2 className="size-4 animate-spin" /> : <Crosshair className="size-4" />}
            label="現在地を使う"
            disabled={isLocating}
            onClick={() => void handleUseCurrent()}
          />
          {results.length > 0 ? (
            <>
              <SectionLabel>検索結果</SectionLabel>
              {results.map((result) => (
                <OptionRow
                  key={result.google_place_id ?? `${result.latitude},${result.longitude}`}
                  icon={<MapPin className="size-4" />}
                  label={result.name}
                  hint={result.address}
                  badge={<VisitedPlaceBadge visitCount={result.visit_count} lastVisitedAt={result.last_visited_at} />}
                  onClick={() => commit({ name: result.name, latitude: result.latitude, longitude: result.longitude })}
                />
              ))}
            </>
          ) : (
            <>
              <SectionLabel>マイプレイス</SectionLabel>
              <MyPlaceOptions
                places={filteredPlaces}
                isLoading={isLoadingMyPlaces}
                onPick={(place) => commit({ name: place.name, latitude: place.latitude, longitude: place.longitude })}
              />
            </>
          )}
        </div>
      )}

      <p className="text-xs tabular-nums text-muted-foreground">
        {value.latitude === null || value.longitude === null
          ? '座標が未設定です。候補から選ぶか検索してください'
          : `${value.latitude.toFixed(5)}, ${value.longitude.toFixed(5)}`}
      </p>
    </div>
  )
}
